import { useMemo } from 'react';
import styled from 'styled-components';
import Card from '@/components/Card';
import { RELATION_EXPRESSION } from '@/apis/order/interface';
import TextComponent from '@/components/TextComponent';
import { formatComma, formatPhoneDefault } from '@/utils/form';
import { formattedDate } from '@/utils/time';
import { getOrderIcon, getOrderLabel } from '@/utils/tableFormat';
import { ReactComponent as ShopBag } from '@/assets/images/order/shopbag.svg';
import BottomButton from './BottomButton';
import { deliveryProcess, pickupProcess } from '@/assets/data/orderList';
import { useHistory } from 'react-router-dom';
import { useEffect } from 'react';
import phamacy from '@/services/phamacy';
import { OrderModel } from '@/services/order';
import { PharmacyAdditionalOrder } from '@/hooks/order/useOrderQuery';
import theme from '@/styles/theme';
import OrderProcess from './OrderProcess';
import WarningNotice from './WarningNotice';
import GlobalAlert from '@/components/GlobalAlert';
import { OrderStatusType } from '@/@types/order';
import useOrderStatus from '@/hooks/common/useOrderStatus';
import AlternativeInfo from './BottomButton/AlternativeInfo/AlternativeInfo';

interface Props {
  order: OrderModel;
  additionalOrderInfo?: {
    hasAdditionalOrder: boolean;
    additionalOrder: PharmacyAdditionalOrder;
  };
}

export interface Agreement {
  dur: boolean;
  useAlternative?: boolean;
  disabledAlternative: boolean;
  guide: boolean;
  pickup: boolean;
}

const OrderInfo = (props: Props) => {
  const { order, additionalOrderInfo } = props;
  const history = useHistory();

  const { orderStatus } = useOrderStatus({ order });

  const isPartnershipPickup = order.orderType === 'partnership_pickup';
  const orderProcess = useMemo(() => {
    if (isPartnershipPickup) {
      return pickupProcess;
    }
    return deliveryProcess;
  }, [order]);

  useEffect(() => {
    if (orderStatus === OrderStatusType.RedirectPreparing) {
      GlobalAlert.show({
        title: '가격 결제 완료',
        body: '조제요청 메뉴로 이동합니다',
        buttons: [
          {
            text: '확인',
            onClick: () => {
              history.replace('/preparing/');
              GlobalAlert.hide();
            }
          }
        ]
      });
    }
  });

  return (
    <Wrapper>
      <WarningNotice />
      <Card>
        <CardBody>
          <OrderType>
            {getOrderIcon(order.orderType)}
            {getOrderLabel(order.orderType)}
          </OrderType>
          <OrderProcess orderProcess={orderProcess} orderStatus={orderStatus} />
          {orderStatus !== OrderStatusType.Ready && order.alternativeLog && (
            <AlternativeInfo alternativeLog={order.alternativeLog} />
          )}
          <BottomButton order={order} orderStatus={orderStatus} />
        </CardBody>
      </Card>
      {order.shipmentInfo && (
        <div style={{ marginTop: 16 }}>
          <Card>
            <CardBody>
              <UserInfo>
                <UserTop>
                  <TextComponent fontSize={24} fontWeight={700}>
                    {order.shipmentInfo.shippingCompanyName}
                  </TextComponent>
                </UserTop>
                <UserBody>
                  {order.shipmentInfo.trackingId && (
                    <OrderInfoRow>
                      <RowLabel>주문번호</RowLabel>
                      <RowContent>{order.shipmentInfo.trackingId}</RowContent>
                    </OrderInfoRow>
                  )}
                  {order.shipmentInfo.riderPhone && (
                    <OrderInfoRow>
                      <RowLabel>배달기사</RowLabel>
                      <RowContent>
                        {order.shipmentInfo.riderName
                          ? `${order.shipmentInfo.riderName} / `
                          : ''}
                        {order.shipmentInfo.riderPhone}
                      </RowContent>
                    </OrderInfoRow>
                  )}
                  {order.shipmentInfo.customerServiceTel && (
                    <OrderInfoRow>
                      <RowLabel>고객센터</RowLabel>
                      <RowContent>
                        {order.shipmentInfo.customerServiceTel}
                      </RowContent>
                    </OrderInfoRow>
                  )}
                  {order.shipmentInfo.operationInfo.length > 0 && (
                    <OrderInfoRow>
                      <RowLabel>운영시간</RowLabel>
                      <RowContentWrapper>
                        {order.shipmentInfo.operationInfo.map(item => (
                          <RowContent>{item}</RowContent>
                        ))}
                      </RowContentWrapper>
                    </OrderInfoRow>
                  )}
                </UserBody>
              </UserInfo>
            </CardBody>
          </Card>
        </div>
      )}
      {additionalOrderInfo?.hasAdditionalOrder && (
        <AdditionalOrderWrapper>
          <AdditionalOrderHeader>
            <ShopBag />
            <AdditionalOrderHeaderText fontSize={16} fontWeight={500}>
              추가 주문이 포함되어 있어요
            </AdditionalOrderHeaderText>
          </AdditionalOrderHeader>
          <AdditionalOrderContent>
            {additionalOrderInfo.additionalOrder.orderItems.map((item, i) => (
              <AdditionalItem
                lastItem={
                  i ===
                  additionalOrderInfo.additionalOrder.orderItems.length - 1
                }>
                <AdditionalImage src={item.thumbnail} alt={item.name} />
                <AdditionalName fontSize={16}>{item.name}</AdditionalName>
                <AdditionalQuantity fontSize={16} fontWeight={600}>
                  {item.quantity}개
                </AdditionalQuantity>
              </AdditionalItem>
            ))}
          </AdditionalOrderContent>
        </AdditionalOrderWrapper>
      )}
      <Card>
        <CardBody>
          <UserInfo>
            <UserTop>
              <span>{order.username}</span>
              <span>{formattedDate(order.createdAt!, 'YYYY-MM-DD HH:mm')}</span>
            </UserTop>
            <UserBody>
              <OrderInfoRow>
                <RowLabel>대리접수</RowLabel>
                <RowContent>
                  접수자 : {order.username} / 환자 :{' '}
                  {order.diagnosis.diagnosisForm.patientName} / 관계 :{' '}
                  {
                    RELATION_EXPRESSION[
                      order.diagnosis.diagnosisForm.patientRelation
                    ]
                  }
                </RowContent>
              </OrderInfoRow>
              <OrderInfoRow>
                <RowLabel>증상/과목</RowLabel>
                <RowContent>{phamacy.getSymptomLabel(order)}</RowContent>
              </OrderInfoRow>
              <OrderInfoRow>
                <RowLabel>연락처</RowLabel>
                <RowContent>{formatPhoneDefault(order.phone)}</RowContent>
              </OrderInfoRow>
              {!isPartnershipPickup && (
                <OrderInfoRow>
                  <RowLabel>주소</RowLabel>
                  <RowContentWrapper>
                    <RowContent>
                      {order.jibunAddress}
                      {order.detailAddress ? ', ' + order.detailAddress : ''}
                    </RowContent>
                    {order.roadAddress && (
                      <RowContentCenter>
                        <RowContent>
                          [도로명] {order.roadAddress}
                          {order.detailAddress
                            ? ', ' + order.detailAddress
                            : ''}
                        </RowContent>
                      </RowContentCenter>
                    )}
                  </RowContentWrapper>
                </OrderInfoRow>
              )}
              <OrderInfoRow>
                <RowLabel>배송방법</RowLabel>
                <RowContent>{getOrderLabel(order.orderType)}</RowContent>
              </OrderInfoRow>
              {order.orderStatus !== OrderStatusType.Ready && (
                <OrderInfoRow>
                  <RowLabel>조제금액</RowLabel>
                  <RowContent>{formatComma(order.price)}원</RowContent>
                </OrderInfoRow>
              )}
              {additionalOrderInfo?.hasAdditionalOrder && (
                <OrderInfoRow>
                  <RowLabel>추가주문금액</RowLabel>
                  <RowContent>
                    {formatComma(
                      additionalOrderInfo.additionalOrder.additionalOrderPrice
                    )}
                    원
                  </RowContent>
                </OrderInfoRow>
              )}
              <OrderInfoRow>
                <RowLabel>진료번호</RowLabel>
                <RowContent>{order.diagnosisExternalId}</RowContent>
              </OrderInfoRow>
              {order.useAlternative && (
                <OrderInfoRow>
                  <RowLabel>대체조제 약품</RowLabel>
                  <RowContent>포함</RowContent>
                </OrderInfoRow>
              )}
            </UserBody>
          </UserInfo>
        </CardBody>
      </Card>
    </Wrapper>
  );
};

export default OrderInfo;

const Wrapper = styled.div`
  width: 100%;
  min-height: 880px;
  flex-direction: column;
  justify-content: space-between;
`;

const OrderType = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  margin-bottom: 40px;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI20SB};
    color: ${theme.DF_COLOR.GRAY900};
  `};
`;

const CardBody = styled.div`
  padding: 32px 40px;
  box-sizing: border-box;
  margin-bottom: 16px;
`;

const UserInfo = styled.div`
  padding: 8px;
`;

const UserTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  & > span:first-of-type {
    display: inline-block;
    ${({ theme }) => theme.DF_TYPOGRAPHY.UI22SB};
    color: ${({ theme }) => theme.DF_COLOR.GRAY900};
  }

  & > span:last-of-type {
    display: inline-block;
    ${({ theme }) => theme.DF_TYPOGRAPHY.UI18R};
    color: ${({ theme }) => theme.DF_COLOR.GRAY500};
  }
`;

const UserBody = styled.div`
  padding-top: 22px;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row;
`;

const OrderInfoRow = styled(Row)`
  margin-bottom: 12px;
`;

const RowLabel = styled.span`
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI18M};
  color: ${({ theme }) => theme.DF_COLOR.GRAY900};
  min-width: 120px;
`;

const RowContentWrapper = styled.div`
  > * {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;
const RowContentCenter = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  margin-top: 5px;

  > * {
    &:nth-child(1) {
      margin-right: 8px;
    }
  }
`;

const RowContent = styled.span`
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI18M};
  color: ${({ theme }) => theme.DF_COLOR.GRAY700};
`;

const AdditionalOrderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
`;

const AdditionalOrderHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 14px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background-color: ${theme.DF_COLOR.PRIMARY700};
`;

const AdditionalOrderHeaderText = styled(TextComponent)`
  color: ${theme.DF_COLOR.WHITE};
  margin-left: 12px;
`;

const AdditionalOrderContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 24px;
  background-color: ${theme.DF_COLOR.WHITE};
`;

const AdditionalItem = styled.div<{ lastItem?: boolean }>`
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: ${({ lastItem }) =>
    lastItem ? '' : `1px dotted ${theme.DF_COLOR.GRAY50}`};
`;

const AdditionalImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 12px;
  margin-right: 12px;
`;

const AdditionalName = styled(TextComponent)`
  margin-right: auto;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AdditionalQuantity = styled(TextComponent)`
  color: ${theme.DF_COLOR.PRIMARY700};
  font-size: 16px;
  font-weight: 500;
`;
